import { CreateAttachmentDependencies } from './createAttachment';
import handleCreateAttachmentNotification from './handleCreateAttachmentNotification';
import { onAttachmentCreationError } from './helpers/onAttachmentCreationHandlers';
import prepareNotificationAndExecuteSendRequest from './helpers/prepareNotificationAndExecuteSendRequest';
import createAttachmentFromUri from '../services/createAttachmentFromUri';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { UriFile } from 'owa-attachment-file-types';
import { ClientAttachmentId, ClientItemId } from 'owa-client-ids';
import { NotificationSubscription } from 'owa-notification';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import NotificationPayloadBase from 'owa-service/lib/contract/NotificationPayloadBase';
import { action } from 'satcheljs/lib/legacy';
import {
    AttachmentFullViewState,
    AttachmentState,
    AttachmentStateType,
    getAttachUriFileCompleteTimeInMS,
    setActionCompletePercent,
    startFakeProgressIndicator,
    stopFakeProgressIndicator,
} from 'owa-attachment-full-data';

export default action('createAttachmentFromUri')(function createAttachmentFromUriProvider(
    parentItemId: ClientItemId,
    attachmentId: ClientAttachmentId,
    attachment: AttachmentType,
    cancellationId: string,
    backingObject: UriFile,
    attachmentState: AttachmentState,
    onAttachmentCreated: (attachment: AttachmentType) => void,
    onAttachmentCreationFailed: () => void,
    dependencies: CreateAttachmentDependencies
): void {
    const perfDatapoint = new PerformanceDatapoint('AttachmentCreationFromUri');
    perfDatapoint.addCustomData({ size: backingObject ? backingObject.size : 0 });

    const logDatapointAndHandleOnAttachmentCreated = (attachment: AttachmentType) => {
        onAttachmentCreated(attachment);
        perfDatapoint.end();
    };

    const logDatapointAndHandleOnAttachmentCreationFailed = (error: Error) => {
        onAttachmentCreationFailed();
        perfDatapoint.endWithError(DatapointStatus.ServerError, error);
    };

    const handleNotification = (notification: NotificationPayloadBase) => {
        handleCreateAttachmentNotification(
            notification,
            parentItemId,
            attachmentId,
            attachment,
            attachmentState,
            logDatapointAndHandleOnAttachmentCreated,
            logDatapointAndHandleOnAttachmentCreationFailed,
            dependencies
        );
    };

    const sendRequest = async (
        subscriptionId: string,
        subscription: NotificationSubscription,
        channelId: string,
        notificationHandler: (notification: NotificationPayloadBase) => void
    ) => {
        try {
            if (attachmentState.attachmentType === AttachmentStateType.Attachment) {
                startFakeProgressIndicator(
                    getAttachUriFileCompleteTimeInMS(backingObject.size),
                    0.05,
                    (completePercent: number) => {
                        setActionCompletePercent(
                            <AttachmentFullViewState>attachmentState,
                            completePercent
                        );
                    },
                    attachmentState.attachmentId
                );
            }

            const guid: string = await createAttachmentFromUri(
                parentItemId,
                backingObject.uri,
                backingObject.name,
                subscriptionId,
                attachment.IsInline,
                null /* contentId */
            );

            if (!guid) {
                throw new Error('Empty response for createAttachmentFromUriProvider request');
            }
        } catch (error) {
            stopFakeProgressIndicator(attachmentState.attachmentId, false);
            onAttachmentCreationFailed();
            onAttachmentCreationError(
                attachmentState,
                dependencies.attachmentWellViewState,
                subscription,
                notificationHandler
            );
        }
    };

    prepareNotificationAndExecuteSendRequest(sendRequest, handleNotification);
});
